import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import Link from 'components/base/Link'
import List, { Theme as ListTheme } from 'components/base/List'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import SectionRule from 'components/base/SectionRule/styles'
import Title from 'components/base/Title'

import AskQuestion from 'components/blocks/AskQuestion'
import Faq from 'components/blocks/Faq'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'

import BoyOnShoulders from 'assets/images/products/BoyOnShoulders.svg'
import BuyingGuide from 'assets/images/products/BuyingGuide.svg'
import GirlsOnSwing from 'assets/images/products/GirlsOnSwing.svg'

import LifeType from 'assets/images/products/LifeType.svg'

const ProductsLifeInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Life Insurance | Insurance Lounge'
      description='Trust the experts at Insurance Lounge to assist you in finding the right life insurance policy. Our team of experts will help you find the perfect fit for your needs.'
      ogImg='/images/og-images/Life.webp'
    />
    <PageHero
      cta={true}
      ctaTitle='Get a life insurance quote now.'
      theme={PageHeroTheme.life}
      trackingInfo={{ page_category: 'product_page_life' }}
    >
      Life Insurance Specialists
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.life} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Life insurance is a valuable asset that can help protect your family members’ financial futures if something unexpected happens to you.
        Finding the right life insurance policy can seem complicated. Learn more about how coverage and pricing work so you can decide which option is best for your family.
      </Paragraph>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Img
        src={GirlsOnSwing}
        src2x={undefined}
        style={{
          height: 240,
          width: 'auto',
        }}
        alt='Girls on Swing'
      />
    </Content>
    <Content short={true} margined={true} center={true}>
      <Title level={2}>
        What Is Life Insurance?
      </Title>
      <Paragraph>
        Life insurance is a contract between an insurer and a policy owner/holder, and it guarantees the insurer pays a sum of money to named beneficiaries when the insured dies
        in exchange for the premiums paid by the policyholder during their lifetime. In addition, state guaranty funds may pay claims if the issuer can’t—a life insurance policy is only as good as the company’s financial strength that issues it to a policyholder.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={LifeType}
          src2x={undefined}
          style={{
            height: 180,
            width: 'auto',
          }}
          alt='Business Type'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>What Are the Different Types of Life Insurance?</Title>
        <Title level={3}>Term life insurance</Title>
        <Paragraph>
          Term life insurance is the most basic form of life insurance. It provides affordable coverage for a set period of time.
          Term insurance is designed to protect against a catastrophic accident or serve as supplemental coverage for a permanent life policy.
          The initial premiums for these policies are usually lower, which is ideal for people with a temporary need for life insurance. This type of life insurance policy will last for a certain number of years, then expires.
          The policyholder has the option to decrease coverage, renew the policy, or convert it to another type of life insurance policy.
        </Paragraph>
        <Title level={3}>Permanent life insurance</Title>
        <Paragraph>
          Permanent life insurance offers protection that will last your entire life, rather than a specific period. Although it can be more expensive than term life insurance,
          it can still provide savings benefits that you can use during your lifetime. The savings portion will build a cash value that you can withdraw from or use to get a loan.
          Whole life insurance is a type of permanent life insurance that accumulates cash value with flexibility for the policyholder. Universal life is another type of permanent
          life insurance with a cash value component that can be adjusted over time; it has level or increasing death benefits. Indexed universal life has a fixed or equity-indexed rate of return on the cash value component.
        </Paragraph>
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Img
        src={BoyOnShoulders}
        src2x={undefined}
        style={{
          height: 240,
          width: 'auto',
        }}
        alt='Boy on Shoulders'
      />
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Which Type of Life Insurance Is Right for You?</Title>
      <Paragraph>
        Before deciding to purchase a life insurance policy, you should first consider your financial standing and family’s needs should they no longer be supported by your income.
      </Paragraph>
      <Title level={2}>What Affects Your Life Insurance Premiums and Costs?</Title>
      <Paragraph>
        When it comes to your life insurance premiums and costs, certain things (age, gender, family medical history) may be beyond your control, but other criteria (smoking, lifestyle)
        can be managed to potentially bring down the cost before applying. Age is the most critical factor because life expectancy is the biggest determinant of risk for the insurance company.
        Additionally, your health history and habits can have a great effect on your life insurance costs. For instance, if you’re a smoker, the risks that come with this habit, such as cancer, mean increased premiums. On the other hand, if you’ve
        made positive lifestyle changes, you can request to be considered for a change in risk class to lower the cost of life insurance. If you’re found to be in better health, then you can expect your premiums to decrease.
      </Paragraph>
    </Content>

    <Content theme={ContentTheme.gray}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={BuyingGuide}
          src2x={undefined}
          style={{
            height: 160,
            width: 'auto',
          }}
          alt='Buying Guide'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Life Insurance Buying Guide</Title>
        <Title level={3}>Choose a type of life insurance to look deeper into</Title>
        <Paragraph>
          Whole life insurance and other forms of permanent life insurance can be useful if the insured wants to provide money for their heirs regardless of when they die.
          Permanent life insurance can also be useful if the insured wants to spend their retirement savings but still leave an inheritance or money for final expenses, such as funeral costs.
        </Paragraph>
        <Paragraph>
          On the other hand, online term life insurance is typically available to healthy individuals and may include questions about the insured’s medical history.
          Term life insurance tends to be less expensive than permanent life insurance and provides insurance for a term that matches the time that people depend on the insured financially.
        </Paragraph>
        <Title level={3}>Determine how much coverage you need</Title>
        <Paragraph>
          Map out your family’s needs. If you plan to use child care for your kids or a cleaning service, you should write this into your coverage calculations.
          It’s best practice to also add coverage for education and include any outstanding mortgage and retirement needs for your spouse in your calculation.
        </Paragraph>
        <Title level={3}>Prepare your application</Title>
        <Paragraph>
          Insurance applications require personal and family medical history, so be prepared to provide this information before starting your application. Applicants will also be required to complete a medical exam.
        </Paragraph>
        <Title level={3}>Compare Life Insurance Quotes</Title>
        <Paragraph>
          To ensure you get the best possible insurance, research multiple life insurance companies to get the best policy for your needs. Price is only part of the equation.
          In order to make sure you’re choosing the right plan, it’s essential to compare different provider’s strengths and weaknesses and compare coverage terms. Remember,
          the plan and provider you choose could have a massive impact on your loved ones one day.
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Faq
          items={[
            {
              question: 'What is better: term or whole life?',
              answer: (
                <>
                  <Paragraph>The truth is, neither is better or worse. The distinction comes depending on what you’re needs are.</Paragraph>
                  <Paragraph>
                    <strong>Reasons to choose term life:</strong>
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      If you only need your plan to cover your income over a certain period. For example, the years you’re raising your family or paying off your mortgage.
                    </li>
                    <li>
                      You’re looking for the most affordable coverage
                    </li>
                    <li>
                      If you are thinking about permanent life insurance but are unable to afford it.
                    </li>
                    <li>
                      If you believe you can invest your money better. Buying a cheaper plan allows you to invest your money in other places you see more fit.
                    </li>
                  </List>
                  <Paragraph>
                    <strong>Reasons to choose whole life:</strong>
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      If you want to provide money for your heirs to pay estate taxes
                    </li>
                    <li>
                      If you have a lifelong dependent, for example, a child with disabilities
                    </li>
                    <li>
                      If you choose to spend your retirement savings but still want to leave an inheritance for your final expenses, like funeral costs
                    </li>
                    <li>
                      If you want to equalize inheritances, whole life insurance could help to compensate each child evenly
                    </li>
                  </List>
                </>
              ),
            },
            {
              question: 'What kinds of death are not covered by life insurance?',
              answer: (
                <Paragraph>
                  Depending on the situation and your policy, some risky behaviors like scuba diving, BASE jumping,
                  hang gliding, auto racing, and mountain climbing may not be covered under your life insurance.
                </Paragraph>
              ),
            },
            {
              question: 'Can life insurance be denied?',
              answer: (
                <>
                  <Paragraph>In most instances, insurance companies pay all benefits to the beneficiary. But unfortunately, there are some reasons why an insurer may deny or delay claims:</Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      Death due to suicide
                    </li>
                    <li>
                      Death due to drug or alcohol abuse
                    </li>
                    <li>
                      Death due to homicide
                    </li>
                    <li>
                      Death due to illegal activity
                    </li>
                    <li>
                      Death due to acts of war
                    </li>
                    <li>
                      Beneficiary was changed after a divorce
                    </li>
                    <li>
                      Beneficiary was not updated after a significant life change
                    </li>
                  </List>
                </>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Paragraph>It’s just as important to discuss what wouldn’t be covered under your plan as it is to discuss what would be covered with your insurance provider.</Paragraph>
      <Title level={2}>Ready to Take the Next Step?</Title>
      <Paragraph>
        Insurance Lounge works to make purchasing life insurance easy and educational. With four retail locations <Link to='/locations/storefronts'>in Oregon</Link>, we’re committed to creating new and effective ways to make getting insurance more transparent.
        Let us work with you to find the best deal for your needs. Call us at (866) 459-0115 or <Link to='/quote/new'>get a free free quote online</Link>.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsLifeInsurance
